"use client";

/**
 * @file        src/components/ui/TabSwitcher.tsx
 * @description Tabs that switch between panels on the same page: a row of
 *              buttons (label, optional second line, optional status dot) and
 *              the panels under it, one visible at a time. Introduced by series
 *              inspection-cases (step 01) for the three report examples of the
 *              inspection block.
 * @universal   TabSwitcher — the ONE in-page tab control of the site.
 *              Registered in knowledge/universals/components.md.
 *              Accepts: tabs[], panels[], ariaLabel, initialIndex, goal.
 * @rule        EVERY PANEL IS IN THE SERVER HTML. Panels arrive as already
 *              rendered nodes and inactive ones only get `hidden`, so a crawler
 *              and an AI reader see all of them without running JavaScript.
 *              Never switch to rendering only the active panel.
 * @rule        NOT FOR FILTERS. A choice that must survive a reload or be
 *              shareable is a URL parameter — that is `FilterTabs`. This control
 *              keeps its state in memory on purpose.
 * @rule        LAYOUT NEVER CLIPS A TAB. One column on a phone, equal columns
 *              from `sm` up (any number of tabs via grid-flow-col) — no
 *              horizontal scroll that hides the last tab behind the edge.
 * @rule        WAI-ARIA tabs pattern: arrows / Home / End move between tabs,
 *              only the active tab is in the Tab order, each tab points to its
 *              panel with aria-controls.
 */

import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { trackGoal } from "@/lib/metrika";
import { cn } from "@/lib/utils";
import { TONE_CLASSES, TONE_SR_LABEL, type StatusTone } from "@/lib/statusTone";

/** One tab button. */
export interface TabItem {
  /** Stable key; also used in element ids. */
  id: string;
  /** Main line, e.g. the car model. */
  label: string;
  /** Optional second line, e.g. the verdict. */
  note?: string;
  /** Optional status dot in front of the label. */
  tone?: StatusTone;
}

interface TabSwitcherProps {
  /** The tabs, in display order. */
  tabs: readonly TabItem[];
  /** Rendered panels, same order and length as `tabs`. */
  panels: readonly ReactNode[];
  /** Accessible name of the tab row, e.g. «Примеры отчётов». */
  ariaLabel: string;
  /** Tab open on first paint. Default 0. */
  initialIndex?: number;
  /** Goal fired when the visitor switches tabs (not on first paint). */
  goal?: string;
  /** Classes for the outer wrapper. */
  className?: string;
}

/**
 * In-page tabs.
 *
 * @input  tabs + panels (equal length), ariaLabel; optional initialIndex, goal.
 * @output A tab row and all panels; only the active panel is visible.
 * @important Panels may be server components — pass them already rendered.
 */
export default function TabSwitcher({
  tabs,
  panels,
  ariaLabel,
  initialIndex = 0,
  goal,
  className,
}: TabSwitcherProps) {
  const [active, setActive] = useState(
    initialIndex >= 0 && initialIndex < tabs.length ? initialIndex : 0,
  );
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const baseId = useId();

  const select = (index: number, focus: boolean) => {
    if (index === active) return;
    setActive(index);
    if (goal) trackGoal(goal);
    if (focus) buttons.current[index]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const last = tabs.length - 1;
    const moves: Record<string, number> = {
      ArrowRight: active === last ? 0 : active + 1,
      ArrowDown: active === last ? 0 : active + 1,
      ArrowLeft: active === 0 ? last : active - 1,
      ArrowUp: active === 0 ? last : active - 1,
      Home: 0,
      End: last,
    };
    if (event.key in moves) {
      event.preventDefault();
      select(moves[event.key], true);
    }
  };

  return (
    <div className={className}>
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="grid grid-cols-1 gap-2 sm:auto-cols-fr sm:grid-flow-col"
      >
        {tabs.map((tab, index) => {
          const selected = index === active;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                buttons.current[index] = el;
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => select(index, false)}
              onKeyDown={onKeyDown}
              className={cn(
                "flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border bg-white px-4 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                selected
                  ? "border-primary ring-1 ring-inset ring-primary"
                  : "border-border hover:border-primary/50",
              )}
            >
              {tab.tone && (
                <span
                  aria-hidden="true"
                  className={cn("h-2.5 w-2.5 shrink-0 rounded-full", TONE_CLASSES[tab.tone].dot)}
                />
              )}
              <span className="min-w-0">
                <span className="block text-sm font-medium text-text">{tab.label}</span>
                {tab.note && (
                  <span className="block text-xs text-text-muted">
                    {tab.tone && <span className="sr-only">{TONE_SR_LABEL[tab.tone]}: </span>}
                    {tab.note}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {panels.map((panel, index) => (
        <div
          key={tabs[index]?.id ?? index}
          role="tabpanel"
          id={`${baseId}-panel-${tabs[index]?.id ?? index}`}
          aria-labelledby={`${baseId}-tab-${tabs[index]?.id ?? index}`}
          hidden={index !== active}
          className="mt-4"
        >
          {panel}
        </div>
      ))}
    </div>
  );
}
